import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Input,
  Typography,
  useTheme,
} from '@mui/material'
import {
  ContractAIBlackIcon,
  ContractAIWhiteIcon,
} from '../../assets/icons/icons'
import { useContext, useEffect, useState } from 'react'
// @ts-ignore
import sanitizeHtml from 'sanitize-html'
import { useUpdateContract } from './hooks'
import SnackBarContext from '../../context/SnackBarContext'

const ContractDetails = ({
  setCurrentState,
  clearData,
  mutationResponse,
  setMutationResponse,
  pdfText,
}: {
  setCurrentState: any
  clearData: any
  mutationResponse?: any
  setMutationResponse?: any
  pdfText?: any
}) => {
  // Use Theme
  const theme = useTheme()
  const { toggleSnackbar } = useContext(SnackBarContext);
  // Use State
  const [prompt, setPrompt] = useState('')
  const [btnLoading, setBtnLoading] = useState(false)

  const [newResponseHTML, setNewResponseHTML] = useState('')

  const { mutate: mutateUpdate, isLoading: isLoading } =
    useUpdateContract('update-contract')

  // HTML Component
  const SafeHtmlTypography = ({ htmlContent }: { htmlContent: any }) => {
    // Optional: Sanitize HTML content
    const safeHtml = sanitizeHtml(
      newResponseHTML ? newResponseHTML : htmlContent,
      {
        allowedTags: ['h1', 'h2', 'h3', 'p', 'img'],
        allowedAttributes: {
          img: ['src', 'alt'],
        },
        transformTags: {
          h1: sanitizeHtml.simpleTransform('h1', tagStyles.h1),
          h2: sanitizeHtml.simpleTransform('h2', tagStyles.h2),
          h3: sanitizeHtml.simpleTransform('h3', tagStyles.h3),
          p: sanitizeHtml.simpleTransform('p', tagStyles.p),
          img: sanitizeHtml.simpleTransform('img', tagStyles.img),
        },
      }
    )

    return <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
  }

  // Handle Process Prompt
  const handleProcessPrompt = async () => {
    setBtnLoading(true)

    const question =
      'Directly generate an HTML document containing the important requirements of the contract. The document should start with an <html> tag and end with a closing </html> tag. Include relevant headings and paragraphs. Do not include any markdown or annotations such as "```html". Return only the HTML content.' +
      prompt

    try {
      const response = await fetch(
        'https://api.openai.com/v1/chat/completions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer sk-proj-cVlF6FquxZa2OT1eFIi8T3BlbkFJjRViQ8vm9wO39k0iJltd`,
          },
          body: JSON.stringify({
            model: 'gpt-4-turbo',
            messages: [{ role: 'user', content: `${pdfText}\n\n${question}` }],
          }),
        }
      )

      const data = await response?.json()
      if (data?.choices && data?.choices?.length > 0) {
        setNewResponseHTML(data?.choices[0]?.message?.content?.trim())
      } else {
        setNewResponseHTML('')
      }
    } catch (error) {
      console.error('Error fetching response from ChatGPT', error)
      setNewResponseHTML('')
    }

    setBtnLoading(false)
  }

  // Handle Save
  const handleSave = () => {
    const payload = {
      id: mutationResponse?.id,
      summary: newResponseHTML ? newResponseHTML : mutationResponse?.summary,
    }

    mutateUpdate(payload, {
      onSuccess: data => {
        setMutationResponse(data)
        toggleSnackbar("contract successfully saved","success")
      },
      onError: () => {
        clearData()
        toggleSnackbar("something went wrong","danger")
        // setCurrentState('upload')
      },
    })
  }

  return (
    <>
      <Card
        sx={{
          padding: 2,
          boxShadow: 'none',
          border: '1px solid #EEEEEE',
          borderRadius: '10px',
          minHeight: '70vh',
          position: 'relative',
        }}>
        <Grid container spacing={2}>
          <Grid item md={9}>
            <Box
              sx={{
                height: '60vh',
                overflowY: 'auto',
                paddingRight: 1,
              }}>
              {btnLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}>
                  {/* <CircularProgress
                    sx={{ color: '#F0845D', width: 50, height: 50 }}
                    size={35}
                  /> */}
                  <Typography
                    sx={{
                      fontSize: '18px',
                      color: '#F0845D',
                      my: 1,
                    }}>
                    Please wait we are proceeding...
                  </Typography>
                </Box>
              ) : (
                <>
                  <SafeHtmlTypography htmlContent={mutationResponse?.summary} />
                </>
              )}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '15px',
                }}>
                <Button
                  variant='contained'
                  sx={{
                    color: '#24212C',
                    background: '#F1EFF5',
                    fontSize: '13px',
                    textTransform: 'none',
                    py: 1.8,
                    width: '19rem',
                    boxShadow: 'none',
                    ':hover': {
                      background: '#F1EFF5',
                      boxShadow: 'none',
                    },
                    borderRadius: 1.8,
                    mr: 3,
                  }}
                  onClick={()=>setCurrentState('upload')}>
                  <ContractAIBlackIcon />{' '}
                  <span style={{ marginLeft: '8px' }}>
                    Process a different contract
                  </span>
                </Button>

                <Button
                  variant='contained'
                  sx={{
                    color: '#FFF',
                    background: '#F0845D',
                    fontSize: '13px',
                    textTransform: 'none',
                    py: 1.8,
                    width: '10rem',
                    boxShadow: 'none',
                    ':hover': {
                      background: '#F0845D',
                      boxShadow: 'none',
                    },
                    borderRadius: 1.8,
                  }}
                  onClick={handleSave}>
                  <span style={{ marginLeft: '8px' }}>
                    {isLoading ? (
                      <CircularProgress
                        sx={{ color: 'white', width: 20, height: 20 }}
                        size={22}
                      />
                    ) : (
                      'Save'
                    )}
                  </span>
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item md={3}>
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#F0845D',
                borderRadius: '12px',
              }}>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '14px',
                }}>
                <ContractAIWhiteIcon />{' '}
                <span style={{ marginLeft: '6px' }}>Edit Prompt</span>
              </Typography>
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #DBDBDB',
                  padding: theme.spacing(1, 1.6, 1, 1.6),
                  mt: 1,
                }}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}>
                <Input
                  disableUnderline
                  multiline
                  sx={{
                    width: '100%',
                    fontSize: '17px',
                    '& input::placeholder': {
                      fontWeight: 'bold',
                    },
                  }}
                  name={'projectDescription'}
                  placeholder='e.g. Outline only important requirements.'
                  type='text'
                  rows={3}
                  fullWidth={true}
                  onChange={(e: any) => setPrompt(e.target.value)}
                  value={prompt}
                  required={true}
                  // disabled={false}
                />
              </Box>
              <Button
                variant='contained'
                sx={{
                  color: '#ffffff',
                  background: '#24212C',
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 1.8,
                  width: '100%',
                  boxShadow: 'none',
                  ':hover': {
                    background: '#24212C',
                    boxShadow: 'none',
                  },
                  borderRadius: 2.2,
                  mt: 2,
                }}
                onClick={handleProcessPrompt}
                // disabled={btnLoading || prompt.length === 0}
              >
                {btnLoading ? (
                  <CircularProgress
                    sx={{ color: 'white', width: 20, height: 20 }}
                    size={22}
                  />
                ) : (
                  <>
                    <ContractAIBlackIcon />{' '}
                    <span style={{ marginLeft: '8px' }}>Process again</span>
                  </>
                )}
              </Button>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#5F5C5F',
                  mt: 1,
                }}>
                Prompt ideas:
                <ul style={{ paddingLeft: '20px', marginBlock: '5px' }}>
                  {[
                    'Extract and summarize key obligations and deadlines.',
                    'Identify all regulatory and compliance requirements.',
                    'Highlight and explain the payment terms and conditions.',
                    'Detect and list potential risks and liabilities detailed in the contract.',
                    'Verify and validate the accuracy of all party information included in the contract.',
                  ]?.map(item => (
                    <li style={{ color: '#7C7A80', fontSize: '14px' }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

export default ContractDetails

const tagStyles = {
  h1: {
    fontSize: '22px',
    color: '#2B262C',
    fontWeight: '600',
  },
  h2: {
    fontSize: '21px',
    color: '#2B262C',
    fontWeight: '600',
  },
  h3: {
    fontSize: '20px',
    color: '#2B262C',
    fontWeight: '600',
  },
  p: {
    fontSize: '15px',
    color: '#7C7A80',
  },
  img: {
    maxWidth: '100%',
    height: 'auto',
  },
}
